import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { areasQueryKey } from "@/lib/areas";
import { candadosQueryKey } from "@/lib/candados";
import { dispararDescarga } from "@/lib/descargas";
import { equiposQueryKey } from "@/lib/equipos";
import { fuentesQueryKey } from "@/lib/fuentes";
import { procedimientosQueryKey } from "@/lib/procedimientos";
import { puntosQueryKey } from "@/lib/puntos";

/**
 * ImportTipo son los maestros con importación ETL xlsx en el backend (#32, manual §8.1).
 * Desde #253 son los SEIS de fábrica —áreas, equipos, fuentes, puntos, candados y
 * procedimientos—, con los mismos nombres que usa la exportación: un solo vocabulario para
 * las dos direcciones. Los usuarios quedan fuera a propósito (D1: el alta es una invitación
 * por correo y es global, no de obra).
 */
export type ImportTipo = "areas" | "equipos" | "fuentes" | "puntos" | "candados" | "procedimientos";

/** ImportRowError es el fallo de UNA fila que no aborta el lote (espejo del backend). */
export type ImportRowError = {
  fila: number;
  motivo: string;
};

/**
 * ImportSummary es el resumen que devuelve POST /api/v1/import/{tipo} (siempre HTTP 200 si el
 * archivo es procesable): filas insertadas (`ok`), omitidas por dedup (`protegidos`) y
 * rechazadas con su motivo (`errores`).
 */
export type ImportSummary = {
  tipo: string;
  ok: number;
  protegidos: number;
  errores: ImportRowError[];
};

// clavesAInvalidar devuelve las claves de caché (POR obra) a invalidar tras importar cada
// tipo. La importación de puntos puede auto-crear la fuente por defecto de un equipo, así
// que también refresca fuentes de la obra.
function clavesAInvalidar(tipo: ImportTipo, obraId: number): readonly (readonly unknown[])[] {
  switch (tipo) {
    case "areas":
      return [areasQueryKey(obraId)];
    case "equipos":
      return [equiposQueryKey(obraId)];
    case "fuentes":
      return [fuentesQueryKey(obraId)];
    case "puntos":
      return [puntosQueryKey(obraId), fuentesQueryKey(obraId)];
    case "candados":
      return [candadosQueryKey(obraId)];
    case "procedimientos":
      return [procedimientosQueryKey(obraId)];
  }
}

/**
 * descargarPlantilla pide GET /api/v1/obras/{obraId}/import/{tipo}/plantilla.xlsx (#253, D5)
 * y dispara la descarga del libro: la cabecera, una fila de ejemplo y una hoja
 * «Instrucciones», todo generado por el servidor desde la misma tabla de columnas que lee el
 * ETL. Mismo gate que la subida (RequireObraAdmin): quien no puede importar no necesita la
 * plantilla.
 */
export async function descargarPlantilla(tipo: ImportTipo, obraId: number): Promise<void> {
  const { blob, nombreArchivo } = await api.getBlob(
    `/obras/${obraId}/import/${tipo}/plantilla.xlsx`,
  );
  // El nombre lo fija el servidor en Content-Disposition (#249 D7); el de aquí es el respaldo
  // por si esa cabecera falta o no es interpretable.
  dispararDescarga(blob, nombreArchivo ?? `plantilla-${tipo}.xlsx`);
}

/**
 * useImportMaestro sube un .xlsx como multipart (campo `file`) a POST
 * /api/v1/obras/{obraId}/import/{tipo} (solo ADMIN, gate real en el backend) y, al terminar,
 * invalida las listas afectadas de la obra para que la tabla refleje las altas. Devuelve el
 * ImportSummary para pintar el resultado.
 */
export function useImportMaestro(tipo: ImportTipo, obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return api.postForm<ImportSummary>(`/obras/${obraId}/import/${tipo}`, form);
    },
    onSuccess: () => {
      for (const queryKey of clavesAInvalidar(tipo, obraId)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
